import { Component, inject, computed, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CustomerService } from '../../core/services/customer.service';
import { FieldService } from '../../core/services/field.service';
import { Customer } from '../../core/models/customer.model';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { PhoneFormatPipe } from '../../shared/pipes';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

@Component({
  selector: 'app-customers',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmModalComponent, PhoneFormatPipe, MaxLengthDirective],
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.scss']
})
export class CustomersComponent {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private customerService = inject(CustomerService);
  private fieldService = inject(FieldService);
  private router = inject(Router);

  // Search
  searchQuery = signal('');

  // Customer to delete
  customerToDelete: Customer | null = null;

  // Computed signals for filtered data
  readonly filteredCustomers = computed(() => {
    const allCustomers = this.customerService.customers();
    const query = this.searchQuery().toLowerCase();

    if (!query) {
      return allCustomers;
    }

    return allCustomers.filter(customer =>
      customer.name.toLowerCase().includes(query) ||
      customer.city.toLowerCase().includes(query) ||
      customer.email.toLowerCase().includes(query)
    );
  });

  // Computed stats
  readonly totalCustomers = computed(() => this.customerService.customers().length);

  readonly totalFields = computed(() => {
    // For now, return total fields from field service
    // Later this will be filtered by customer
    return this.fieldService.fields().length;
  });

  readonly totalAcreage = computed(() => {
    return this.fieldService.fields().reduce((sum, f) => sum + f.acreage, 0);
  });

  // Navigation
  addCustomer(): void {
    this.router.navigate(['/customers/add']);
  }

  viewCustomer(customer: Customer): void {
    this.router.navigate(['/customers', customer.id, 'view']);
  }

  editCustomer(customer: Customer): void {
    this.router.navigate(['/customers', customer.id, 'edit']);
  }

  // Delete flow
  deleteCustomer(customer: Customer): void {
    this.customerToDelete = customer;
    this.confirmModal.open({
      title: 'Delete Customer',
      message: `Are you sure you want to delete "${customer.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  onDeleteConfirmed(): void {
    if (this.customerToDelete) {
      this.customerService.deleteCustomer(this.customerToDelete.id);
      this.customerToDelete = null;
    }
  }

  onDeleteCancelled(): void {
    this.customerToDelete = null;
  }

  // Filter helpers
  clearFilters(): void {
    this.searchQuery.set('');
  }

  // Format phone display
  formatPhone(phone: string): string {
    return phone || '-';
  }

  // Format location display
  formatLocation(customer: Customer): string {
    if (customer.city && customer.state) {
      return `${customer.city}, ${customer.state}`;
    }
    return customer.city || customer.state || '-';
  }

  // Get field count for a customer
  getFieldCount(customerId: string): number {
    return this.fieldService.getFieldsByCustomerId(customerId).length;
  }

  // Get total acreage for a customer
  getCustomerAcreage(customerId: string): number {
    return this.fieldService.getFieldsByCustomerId(customerId).reduce((sum, f) => sum + f.acreage, 0);
  }
}
